import React, { createContext, useContext, useReducer, useEffect } from 'react';

const AssetContext = createContext();

const initialState = {
  assets: [],
  exchanges: {
    binance: { apiKey: '', secret: '', enabled: false },
    okx: { apiKey: '', secret: '', passphrase: '', enabled: false },
    hyperliquid: { apiKey: '', enabled: false }, // Removed secret field for Hyperliquid
    aster: { apiKey: '', secret: '', enabled: false },
    defiWallets: []
  },
  loading: false,
  lastUpdated: null
};

function assetReducer(state, action) {
  switch (action.type) {
    case 'SET_ASSETS':
      return { ...state, assets: action.payload, lastUpdated: new Date().toISOString() };
    case 'UPDATE_EXCHANGE_CONFIG':
      return {
        ...state,
        exchanges: {
          ...state.exchanges,
          [action.payload.exchange]: {
            ...state.exchanges[action.payload.exchange],
            ...action.payload.config
          }
        }
      };
    case 'TOGGLE_EXCHANGE':
      return {
        ...state,
        exchanges: {
          ...state.exchanges,
          [action.payload]: {
            ...state.exchanges[action.payload],
            enabled: !state.exchanges[action.payload].enabled
          }
        }
      };
    case 'ADD_DEFI_WALLET':
      return {
        ...state,
        exchanges: {
          ...state.exchanges,
          defiWallets: [...state.exchanges.defiWallets, action.payload]
        }
      };
    case 'REMOVE_DEFI_WALLET':
      return {
        ...state,
        exchanges: {
          ...state.exchanges,
          defiWallets: state.exchanges.defiWallets.filter(wallet => wallet !== action.payload)
        }
      };
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'LOAD_FROM_STORAGE':
      return { ...initialState, ...action.payload };
    default:
      return state;
  }
}

export const AssetContextProvider = ({ children }) => {
  const [state, dispatch] = useReducer(assetReducer, initialState);

  // Load from localStorage on mount
  useEffect(() => {
    const savedState = localStorage.getItem('cryptoAssetTracker');
    if (savedState) {
      try {
        const parsed = JSON.parse(savedState);
        // Only load exchanges config and assets, not loading states
        dispatch({ type: 'LOAD_FROM_STORAGE', payload: parsed });
      } catch (e) {
        console.error('Failed to load saved state', e);
      }
    }
  }, []);

  // Save to localStorage whenever state changes
  useEffect(() => {
    const { loading, ...saveableState } = state;
    localStorage.setItem('cryptoAssetTracker', JSON.stringify(saveableState));
  }, [state]);

  return (
    <AssetContext.Provider value={{ state, dispatch }}>
      {children}
    </AssetContext.Provider>
  );
};

export const useAssetContext = () => {
  const context = useContext(AssetContext);
  if (!context) {
    throw new Error('useAssetContext must be used within AssetContextProvider');
  }
  return context;
};